import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '../../features/auth/services/auth.service';
import { MatchAccessTokenService } from '../services/match-access-token.service';

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
const XSRF_COOKIE = 'XSRF-TOKEN';
const XSRF_HEADER = 'X-XSRF-TOKEN';
const MATCH_ACCESS_TOKEN_HEADER = 'X-Match-Access-Token';

const REFRESH_SKIP_PATHS = [
  '/api/auth/refresh',
  '/api/auth/login',
  '/api/auth/logout',
  '/api/auth/me',
];

// Routes whose pages are authorised by a match access token. The match id comes
// from the page, not the request: a write like PUT /api/matchdata/418/ carries a
// child id, so the URL alone cannot say which match it belongs to.
const MATCH_ROUTE_PATTERNS = [
  /^\/scoreboard\/match\/(\d+)(?:\/|$)/,
  /^\/statkeeper\/match\/(\d+)(?:\/|$)/,
  /^\/sports\/\d+\/matches\/(\d+)(?:\/|$)/,
];

/** Match id of the page currently being viewed, or null when it is not a match page. */
function currentRouteMatchId(): number | null {
  if (typeof document === 'undefined') return null;
  const path = document.location?.pathname ?? '';
  for (const pattern of MATCH_ROUTE_PATTERNS) {
    const found = pattern.exec(path);
    if (found) return Number(found[1]);
  }
  return null;
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  // Plain string parse instead of a constructed RegExp: `document.cookie` is
  // '; '-separated per spec, so an exact-name comparison on each pair does the
  // same job without dynamic regex building (flagged by semgrep, and the old
  // escape-then-construct dance was harder to audit than a split).
  for (const part of document.cookie.split('; ')) {
    const eq = part.indexOf('=');
    if (eq > -1 && part.slice(0, eq) === name) {
      return decodeURIComponent(part.slice(eq + 1));
    }
  }
  return null;
}

function withCookiesAndXsrf(req: HttpRequest<unknown>): HttpRequest<unknown> {
  const updates: { withCredentials: boolean; setHeaders?: Record<string, string> } = {
    withCredentials: true,
  };
  if (!SAFE_METHODS.has(req.method.toUpperCase())) {
    const xsrf = readCookie(XSRF_COOKIE);
    if (xsrf && !req.headers.has(XSRF_HEADER)) {
      updates.setHeaders = { [XSRF_HEADER]: xsrf };
    }
  }
  return req.clone(updates);
}

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);
  const accessTokenService = inject(MatchAccessTokenService);

  // Match-scoped public access token: send via X-Match-Access-Token header whenever the
  // page being viewed has a validated token, regardless of session state. Match access
  // tokens are a separate capability, unrelated to user JWT/cookie auth — a share link
  // must keep working for a signed-in visitor who is neither owner nor admin/editor
  // (FAS-9ym8). Withholding it on `isAuthenticated()` let the guard and the UI grant
  // editing while every write fell through to the JWT path and 403'd.
  //
  // Scoped to the current page's match on purpose: the token is only valid for its own
  // match, and the backend prefers the token path over the session, so leaking match A's
  // token onto a request for match B would turn a request the caller's own session would
  // have satisfied into a 403.
  const routeMatchId = currentRouteMatchId();
  const matchAccessToken =
    routeMatchId === null ? null : accessTokenService.getTokenForMatch(routeMatchId);
  let workingReq = req;
  if (matchAccessToken && !req.headers.has(MATCH_ACCESS_TOKEN_HEADER)) {
    workingReq = req.clone({
      setHeaders: { [MATCH_ACCESS_TOKEN_HEADER]: matchAccessToken },
    });
  }

  const authReq = withCookiesAndXsrf(workingReq);

  return next(authReq).pipe(
    catchError((err: unknown) => {
      if (
        err instanceof HttpErrorResponse &&
        err.status === 401 &&
        authService.isAuthenticated() &&
        !REFRESH_SKIP_PATHS.some((p) => authReq.url.includes(p))
      ) {
        return authService.refreshSession().pipe(
          switchMap(() => next(withCookiesAndXsrf(workingReq))),
        );
      }
      return throwError(() => err);
    }),
  );
};
